/**
 * Lot AK (décision de Maxime du 14/10/2026) : une personne par TYPE de carte de « À faire », réglable dans
 * Réglages > Règles des actions (une ligne par type). Remplace les trois groupes d'avant (`billing` facturation
 * et impayés, `sales` relances et devis, `admin` abonnements-domaines-dépenses), gardés en lecture : un type
 * jamais réglé prend la personne de son ancien groupe, puis l'administrateur (rien ne change tant qu'on ne règle
 * pas). Les règles qui ont leur propre personne (étapes du projet `whoUserId`, rendez-vous `meetingsUserId`)
 * gardent leur logique.
 *
 * Valeurs enregistrées (`settings.rules.attribution`) :
 * - clé ABSENTE : jamais réglée, repli sur l'ancien groupe ;
 * - `null` : « L'administrateur » choisi explicitement ;
 * - un identifiant de membre : cette personne (si elle n'est plus active, l'administrateur).
 */

/** Anciens groupes (avant le lot AK), lus seulement comme repli. */
export type LegacyAttributionKey = 'billing' | 'sales' | 'admin';
export const LEGACY_ATTRIBUTION_KEYS = ['billing', 'sales', 'admin'] as const;

/** Types de carte (clés stables, enregistrées dans `settings.rules.attribution`). */
export const ATTRIBUTION_KEYS = [
  'invoice',
  'unpaid',
  'subscriptionSetup',
  'quoteFollowup',
  'quoteToSend',
  'projectPlan',
  'review',
  'partner',
  'deadline',
  'expenseCategorize',
  // Lot AO : « Préparer les R1 » (carte « Compléter le R1 »), non réglé = les membres présents sur le R1.
  'r1Prep',
  // Lot AT : « Préparer les devis » (carte « Préparer le devis » après le R1), non réglé = les membres présents.
  'quotePrepare',
  // Lot BB : conversations de support à traiter (non réglé : l'administrateur, Maxime).
  'support',
  // Lot BB : alertes importantes des fournisseurs (non réglé : l'administrateur).
  'supplier',
] as const;
export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];

/** Groupes d'affichage des lignes (Réglages). */
export type AttributionGroup = 'facturation' | 'ventes' | 'gestion' | 'support';
export const ATTRIBUTION_GROUP_LABELS: Record<AttributionGroup, string> = {
  facturation: 'Facturation et abonnements',
  ventes: 'Devis, projets et clients',
  gestion: 'Gestion',
  support: 'Support et fournisseurs',
};

export interface AttributionType {
  key: AttributionKey;
  /** Libellé de la ligne (Réglages, fiches du guide). */
  label: string;
  /** Les cartes concernées, en clair. */
  hint: string;
  group: AttributionGroup;
  /**
   * Ancien groupe : repli quand le type n'a jamais été réglé. Absent (lot AO, « Préparer les R1 ») : un type
   * jamais réglé n'a pas de personne unique (`unsetLabel`), la règle choisit elle-même (membres présents).
   */
  legacy?: LegacyAttributionKey;
  /** Lot AO : libellé du choix « non réglé » quand il ne désigne pas l'administrateur (« Les membres présents »). */
  unsetLabel?: string;
  /** Règles (`ruleKey`) dont les cartes vont à cette personne. */
  ruleKeys: readonly string[];
}

/** La liste des types, dans l'ordre d'affichage (une seule source pour les Réglages, les fiches et l'assistant). */
export const ATTRIBUTION_TYPES: readonly AttributionType[] = [
  {
    key: 'invoice',
    label: 'Facturer',
    hint: 'acompte, solde, abonnements hors Stripe, échéances de facturation du Sheet',
    group: 'facturation',
    legacy: 'billing',
    ruleKeys: ['invoice_create.deposit', 'invoice_create.balance', 'manual_subscription_invoice'],
  },
  {
    key: 'unpaid',
    label: 'Impayés et paiements refusés',
    hint: 'rappels de paiement, prélèvements Stripe refusés',
    group: 'facturation',
    legacy: 'billing',
    ruleKeys: ['unpaid_reminder', 'payment_failed'],
  },
  {
    key: 'subscriptionSetup',
    label: 'Mettre en place l’abonnement',
    hint: 'après le R5 ou un site en ligne sans abonnement',
    group: 'facturation',
    legacy: 'admin',
    ruleKeys: ['subscription_setup'],
  },
  {
    key: 'quoteFollowup',
    label: 'Relances de devis',
    hint: 'sauf client qui a son responsable des relances',
    group: 'ventes',
    legacy: 'sales',
    ruleKeys: ['quote_followup'],
  },
  {
    key: 'quoteToSend',
    label: 'Devis à envoyer et R2 à fixer',
    hint: 'après le R1 ou le R2 de l’agenda ; chance de signature après un R2 sans membre présent connu',
    group: 'ventes',
    legacy: 'sales',
    ruleKeys: ['quote_to_send', 'meeting_r2_missing', 'quote_win_chance'],
  },
  {
    key: 'projectPlan',
    label: 'Préparer le projet',
    hint: 'devis signé sans projet ; aussi les étapes et rendez-vous sans personne',
    group: 'ventes',
    legacy: 'sales',
    ruleKeys: ['project_plan'],
  },
  {
    key: 'review',
    label: 'Demandes d’avis et anniversaires',
    hint: 'avis Google, anniversaires de mise en ligne',
    group: 'ventes',
    legacy: 'sales',
    ruleKeys: ['review_request', 'info.anniversary'],
  },
  {
    key: 'partner',
    label: 'Apporteurs',
    hint: 'canal ou apporteur à renseigner, noms d’apporteurs à reconnaître',
    group: 'ventes',
    legacy: 'sales',
    ruleKeys: ['partner_missing', 'partner_unknown'],
  },
  {
    key: 'deadline',
    label: 'Échéances du Google Sheet',
    hint: 'domaines, renouvellements (une échéance de facturation suit « Facturer »)',
    group: 'gestion',
    legacy: 'admin',
    ruleKeys: ['deadline'],
  },
  {
    key: 'expenseCategorize',
    label: 'Dépenses à catégoriser',
    hint: 'lignes bancaires sans poste',
    group: 'gestion',
    legacy: 'admin',
    ruleKeys: ['expense_categorize'],
  },
  {
    key: 'r1Prep',
    label: 'Préparer les R1',
    hint: 'infos à compléter avant un R1 ; non réglé : chaque membre présent sur le R1',
    group: 'ventes',
    unsetLabel: 'Les membres présents sur le R1',
    ruleKeys: ['r1_prep'],
  },
  {
    key: 'quotePrepare',
    label: 'Préparer les devis',
    hint: 'devis à préparer après le R1 ; non réglé : chaque membre présent sur le R1',
    group: 'ventes',
    unsetLabel: 'Les membres présents sur le R1',
    ruleKeys: ['quote_prepare'],
  },
  {
    key: 'support',
    label: 'Support',
    hint: 'conversations Crisp et mails des clients qui attendent une réponse ; relances des mails partis de contact@',
    group: 'support',
    ruleKeys: ['support_conversation', 'mail_followup'],
  },
  {
    key: 'supplier',
    label: 'Alertes des fournisseurs',
    hint: 'mails importants d’OVH, de Clever Cloud… (domaine à valider, incident)',
    group: 'support',
    ruleKeys: ['supplier_alert'],
  },
];

export const ATTRIBUTION_LABELS: Record<AttributionKey, string> = Object.fromEntries(
  ATTRIBUTION_TYPES.map((t) => [t.key, t.label]),
) as Record<AttributionKey, string>;

/**
 * Personne de chaque type de carte, résolue (identifiant d'un membre actif, null s'il n'y a aucun membre). Lot AO :
 * pour un type sans ancien groupe (`r1Prep`), null = jamais réglé (la règle choisit : les membres présents).
 */
export type Attribution = Record<AttributionKey, string | null>;

/** Ce qui est enregistré : les anciens groupes et les types, chacun absent, null ou un identifiant. */
export type StoredAttribution = Partial<Record<LegacyAttributionKey | AttributionKey, string | null>>;

/**
 * Valeur ENREGISTRÉE d'un type, avant la vérification des membres actifs : le type s'il a été réglé (null =
 * l'administrateur), sinon son ancien groupe, sinon null (l'administrateur). C'est aussi la valeur affichée par
 * les Réglages.
 */
export function storedAttribution(
  stored: StoredAttribution | null | undefined,
  key: AttributionKey,
): string | null {
  const value = stored?.[key];
  if (value !== undefined) return value;
  const legacy = ATTRIBUTION_TYPES.find((t) => t.key === key)?.legacy;
  return (legacy && stored?.[legacy]) || null;
}

/**
 * Personne de chaque type : la valeur enregistrée (`storedAttribution`) si c'est un membre actif, sinon
 * l'administrateur (`fallbackId`).
 */
export function resolveAttributionIds(
  stored: StoredAttribution | null | undefined,
  activeIds: ReadonlySet<string>,
  fallbackId: string | null,
): Attribution {
  return Object.fromEntries(
    ATTRIBUTION_KEYS.map((key) => {
      // Lot AO : type sans ancien groupe jamais réglé (clé absente) : pas de personne unique.
      if (stored?.[key] === undefined && ATTRIBUTION_TYPES.find((t) => t.key === key)?.unsetLabel)
        return [key, null];
      const id = storedAttribution(stored, key);
      return [key, id && activeIds.has(id) ? id : fallbackId];
    }),
  ) as Attribution;
}

/**
 * La même personne pour tous les types d'un ancien groupe (`{ billing, sales, admin }` → par type) : tests et
 * données de démonstration. Lot BB : un type sans ancien groupe ni choix « non réglé » propre (support,
 * fournisseurs) prend `fallbackId` (l'administrateur, comme `resolveAttributionIds`).
 */
export function attributionFromBuckets(
  buckets: Record<LegacyAttributionKey, string | null>,
  fallbackId: string | null = null,
): Attribution {
  return Object.fromEntries(
    ATTRIBUTION_TYPES.map((t) => [t.key, t.legacy ? buckets[t.legacy] : t.unsetLabel ? null : fallbackId]),
  ) as Attribution;
}
